<template lang="pug">
  .support-inbox(v-loading="loading")
    .support-page-header
      h1 Soporte · Bandeja
      .support-page-actions
        el-tag(v-if="pendingCount > 0" type="warning") 🔔 {{ pendingCount }} pendientes
        el-button(size="small" @click="reload") ⟲ Refrescar

    .support-filters
      el-select(v-model="filters.status" placeholder="Estado" clearable size="small" @change="reload")
        el-option(label="Pendientes (esperando humano)" value="waiting_human")
        el-option(label="En chat" value="human_handling")
        el-option(label="Cerradas" value="resolved")
        el-option(label="Bot manejando" value="bot_handling")
      el-select(v-model="filters.assignedTo" placeholder="Asignación" clearable size="small" @change="reload")
        el-option(label="A mí" value="me")
        el-option(label="Sin asignar" value="unassigned")

    el-table(:data="docs" stripe @row-click="openConversation")
      el-table-column(label="Estado" width="160")
        template(slot-scope="s")
          el-tag(:type="statusTagType(s.row.status)" size="mini") {{ statusLabel(s.row.status) }}
      el-table-column(label="Usuario" min-width="200")
        template(slot-scope="s")
          div {{ userDisplay(s.row.user) }}
          .support-inbox-meta {{ s.row.userRole }}
      el-table-column(prop="context.moduleHint" label="Módulo" width="160")
      el-table-column(label="Inicio" width="120")
        template(slot-scope="s") {{ formatRelative(s.row.startedAt) }}
      el-table-column(label="Asignado a" width="160")
        template(slot-scope="s") {{ userDisplay(s.row.assignedTo) || '—' }}
      el-table-column(label="Acción" width="120" align="right")
        template(slot-scope="s")
          el-button(v-if="!s.row.assignedTo" size="mini" type="primary" @click.stop="takeConversation(s.row)") Tomar
          el-button(v-else size="mini" @click.stop="openConversation(s.row)") Abrir

    el-pagination(
      v-if="total > limit"
      background
      layout="prev, pager, next"
      :total="total"
      :page-size="limit"
      :current-page.sync="page"
      @current-change="reload"
    )
</template>

<script>
import supportService from "@/services/support";

/**
 * @component SupportInbox
 * @description Bandeja del operador de soporte (Fase 5 — UC-REG-098).
 *              Lista conversaciones del tenant con filtros. Auto-poll
 *              cada 10s para que aparezcan los nuevos. Click en fila →
 *              detalle de conversación.
 * @example <SupportInbox />
 */
export default {
  name: "SupportInbox",
  data() {
    return {
      docs: [],
      total: 0,
      page: 1,
      limit: 20,
      loading: false,
      filters: {
        status: "waiting_human",
        assignedTo: null,
      },
      pollTimer: null,
    };
  },
  computed: {
    pendingCount() {
      return this.docs.filter((d) => d.status === "waiting_human").length;
    },
  },
  mounted() {
    this.reload();
    this.pollTimer = setInterval(this.reload, 10000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    /**
     * @description Trae la bandeja con filtros.
     */
    async reload() {
      this.loading = true;
      try {
        const data = await supportService.listConversations({
          ...this.filters,
          page: this.page,
          limit: this.limit,
        });
        this.docs = data.docs;
        this.total = data.total;
      } catch (err) {
        this.$message.error("No pude cargar la bandeja.");
      } finally {
        this.loading = false;
      }
    },
    /**
     * @description Toma una conversación (assign a sí mismo) y abre.
     * @param {Object} row
     */
    async takeConversation(row) {
      try {
        await supportService.assignConversation(row._id, "me");
        this.openConversation(row);
      } catch (err) {
        this.$message.error("No pude tomarla.");
      }
    },
    /**
     * @description Navega al detalle.
     * @param {Object} row
     */
    openConversation(row) {
      this.$router.push({
        name: "support-conversation",
        params: { id: row._id },
      });
    },
    /**
     * @description Display del nombre completo de un user populado.
     * @param {Object} u
     * @returns {string}
     */
    userDisplay(u) {
      if (!u) return "";
      return (
        `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username || u._id
      );
    },
    /**
     * @description Color del tag según status.
     * @param {string} s
     */
    statusTagType(s) {
      if (s === "waiting_human") return "warning";
      if (s === "human_handling") return "";
      if (s === "resolved") return "success";
      if (s === "bot_handling") return "info";
      return "info";
    },
    statusLabel(s) {
      const map = {
        bot_handling: "🤖 bot",
        waiting_human: "🟠 esperando",
        human_handling: "🟡 en chat",
        resolved: "✅ resuelta",
        abandoned: "⚪ abandonada",
      };
      return map[s] || s;
    },
    formatRelative(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
      if (diffMin < 60) return `${diffMin} min`;
      const diffHr = Math.round(diffMin / 60);
      if (diffHr < 48) return `${diffHr} h`;
      return `${Math.round(diffHr / 24)} d`;
    },
  },
};
</script>

<style lang="scss" scoped>
.support-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  h1 {
    margin: 0;
    font-size: 22px;
    color: #002d5e;
  }
}
.support-page-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}
.support-filters {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.support-inbox-meta {
  font-size: 11px;
  color: #6b7294;
}
</style>
